import { ALL_WEEK_DAYS, WeekDayNames } from "../entities/hours";
import { Period } from "../entities/period";
import {
  PRESENCE_STATUS_ACCEPTED,
  PresenceStatus,
  PresenceType
} from "../entities/presence";
import { ResourceType } from "../entities/resource";
import { ISchedule } from "../entities/schedule";
import {
  CreateOrUpdateScheduleFromIntegrationsRequest,
  FromIntegrationsScheduleRequest,
  ScheduleRequest,
  ScheduleRequestUpdate,
  SchedulesFilter
} from "../entities/schedule_request";
import { ScheduleType } from "../entities/schedule_type";
import { IUserFull } from "../entities/user";

import {
  IOfficeUsageFormatted,
  IOfficeUsageFull,
  IOfficesSchedules,
  IOfficesSchedulesIndexed,
  IOfficesSchedulesResourceDay,
  IWorkingLocationPresenceType,
  IWorkingLocations,
  ResGetOfficesNews,
  ResGetOfficesQuestionnaire,
  ResGetOfficesSchedulesMe,
  ResGetOfficesSchedulesOccupied,
  ResGetOfficesSchedulesPending,
  ResOfficesSchedulesOccupiedUsage
} from "../types/api-types";
import { expandQueryDays, getDaysWithinTimespan } from "../utils";
import { BaseOrganizationEndpoints } from "./base";
import { Id } from "./response/id";
import { ResponseWithStatus } from "./response/response_with_status";

export type ScheduleParamsAll = {
  schedule?: string[];
  team?: string[];
  office?: string[];
  office_id?: string;
  resource?: string[]; // THIS IS THE FLOOR
  resource_id?: string[] | string;
  presence?: string[];
  status?: string[];
  user?: string[];
  type?: ResourceType[];
  schedule_type?: ScheduleType[];
  slot_type?: ScheduleType[];
  date?: string;
  tz?: string;
  cursor?: string;
  limit?: number;
  weeks?: number;
  start?: string;
  end?: string;
  days?: number;
  isForOthers?: boolean;
  only_inactive_users?: boolean;
};

export type ScheduleFilterType =
  | "checkin"
  | "managed"
  | "me"
  | "occupied"
  | "pending"
  | "scheduler";

export class Schedules extends BaseOrganizationEndpoints {
  private static readonly DEFAULT_NUMBER_OF_WEEKS: number = 1;

  protected formatScheduleParams(
    params: { [idx: string]: string | string[] | number | boolean } = {}
  ) {
    const urlParams = this.formatGenericParams(params);
    if (!urlParams.has("weeks")) {
      urlParams.set("weeks", Schedules.DEFAULT_NUMBER_OF_WEEKS.toString());
    }
    urlParams.set("fetchSimple", "1");
    return urlParams;
  }

  processFilter(requestUrl: string, filter: SchedulesFilter) {
    if (filter.schedule_statuses) {
      for (const status of filter.schedule_statuses) {
        requestUrl += `&status=${encodeURIComponent(status)}`;
      }
    }

    if (filter.schedule_types) {
      for (const schedule_type of filter.schedule_types) {
        //if(schedule_type)
        requestUrl += `&schedule_type=${encodeURIComponent(schedule_type)}`;
      }
    }

    if (filter.slot_types) {
      for (const slot_type of filter.slot_types) {
        if (slot_type)
          requestUrl += `&slot_type=${encodeURIComponent(slot_type)}`;
      }
    }

    if (filter.type) {
      for (const type of filter.type) {
        requestUrl += `&type=${encodeURIComponent(type)}`;
      }
    }

    if (filter.is_for_others) {
      requestUrl += "&isForOthers=true";
    }

    if (filter.tz) {
      requestUrl += `&tz=${encodeURIComponent(filter.tz)}`;
    }

    return requestUrl;
  }

  /** @deprecated use `all(...)` */
  async schedules(
    office_id: string,
    start: string,
    end: string,
    user_id: string | null = null,
    status_filter: string[] = ["accepted", "pending"]
  ) {
    let requestUrl =
      `/offices/${encodeURIComponent(office_id)}/schedules?` +
      `start=${encodeURIComponent(start)}` +
      `&end=${encodeURIComponent(end)}`;

    for (const filter of status_filter) {
      requestUrl += `&presence=${encodeURIComponent(filter)}`;
    }

    for (const filter of status_filter) {
      requestUrl += `&status=${encodeURIComponent(filter)}`;
    }

    if (user_id !== null) {
      requestUrl += `&user=${encodeURIComponent(user_id)}`;
    }

    const result = await this.requestWithStatus<IOfficesSchedulesIndexed>(
      "GET",
      requestUrl
    );
    return result;
  }

  // Duplicate of organizations.questionnaire
  async questionnaire(office_id: string) {
    const result = await this.requestWithStatus<ResGetOfficesQuestionnaire>(
      "GET",
      `/offices/${encodeURIComponent(office_id)}/questionnaire`
    );
    return result;
  }

  async news(office_id: string) {
    const result = await this.requestWithStatus<ResGetOfficesNews>(
      "GET",
      `/offices/${encodeURIComponent(office_id)}/news`
    );
    return result;
  }

  /** @deprecated Use `all(...)` instead */
  async me(
    office_id: string,
    time_zone: string | null = null,
    date: string | null = null,
    params: {
      limit?: number;
      weeks?: number;
      type?: ResourceType[];
    } = {}
  ) {
    const urlParams = new URLSearchParams();
    if (date) {
      urlParams.set("date", date);
    }
    urlParams.set(
      "weeks",
      (params.weeks ?? Schedules.DEFAULT_NUMBER_OF_WEEKS).toString()
    );
    if (params.limit) {
      urlParams.set("limit", params.limit.toString());
    }
    if (params.type !== null && params.type !== undefined) {
      for (let i = 0; i < params.type?.length; i++) {
        urlParams.append("type", params.type[i]);
      }
    }
    const result = await this.requestWithStatus<ResGetOfficesSchedulesMe>(
      "GET",
      `/offices/${office_id}/schedules/me?${urlParams.toString()}`
    );
    return result;
  }

  async all(
    filter: ScheduleFilterType | "",
    params: ScheduleParamsAll,
    useCore = false
  ) {
    const urlParams = this.formatScheduleParams(params);

    let url = `${this.settings.adminAuth ? "/integrations" : ""}/schedules/${filter}?${urlParams.toString()}`;
    if (filter === "occupied" && useCore) {
      url = `/v1/schedules/occupied?${urlParams.toString()}`;
    } else if (filter === "managed") {
      // 'managed' endpoint is now in core
      url = `/v1/schedules/managed?${urlParams.toString()}`;
    } else if (filter === "pending") {
      // 'pending' endpoint is now in core
      urlParams.set("only_pending", "");
      url = `/v1/schedules/managed?${urlParams.toString()}`;
    }

    return this.requestWithStatus<IOfficesSchedules>("GET", url);
  }

  /**
   * The caller's own reservations between two dates (`/v1/me/schedules`).
   *
   * Unlike `all("managed", ...)` this matches on slot membership rather than
   * on being the organizer, so a desk booked on the caller's behalf is
   * included; and unlike the agenda it is not capped to a single week. The
   * server rejects ranges longer than 366 days.
   */
  async mine(params: {
    start: string;
    end: string;
    tz: string;
    type?: ResourceType[];
    limit?: number;
    cursor?: string;
  }) {
    const urlParams = new URLSearchParams();
    urlParams.set("start", params.start);
    urlParams.set("end", params.end);
    urlParams.set("tz", params.tz);
    for (const type of params.type ?? []) {
      urlParams.append("type", type);
    }
    if (params.limit) {
      urlParams.set("limit", params.limit.toString());
    }
    if (params.cursor) {
      urlParams.set("cursor", params.cursor);
    }
    return this.requestWithStatus<IOfficesSchedules>(
      "GET",
      `/v1/me/schedules?${urlParams.toString()}`
    );
  }

  /**
   * Every reservation on one resource between two dates
   * (`/v1/resources/:id/schedules`), whoever made it.
   *
   * Backs the admin prompts that have to show what a change to a resource
   * would disrupt — dedicating a desk above all, since that makes the desk
   * unbookable. Requires the resource-update permission.
   */
  async forResource(
    resourceId: string,
    params: {
      start: string;
      end: string;
      tz: string;
      limit?: number;
      cursor?: string;
    }
  ) {
    const urlParams = new URLSearchParams();
    urlParams.set("start", params.start);
    urlParams.set("end", params.end);
    urlParams.set("tz", params.tz);
    if (params.limit) {
      urlParams.set("limit", params.limit.toString());
    }
    if (params.cursor) {
      urlParams.set("cursor", params.cursor);
    }
    return this.requestWithStatus<IOfficesSchedules>(
      "GET",
      `/v1/resources/${encodeURIComponent(resourceId)}/schedules?${urlParams.toString()}`
    );
  }

  /**
   * Every reservation on a dedicated desk between two dates, the same listing
   * as `forResource` but open to the desk's own dedicated user as well as to
   * resource managers: before releasing the desk, or taking a release back,
   * they need to see whose reservations that cancels.
   */
  async forDedicatedResource(
    resourceId: string,
    params: {
      start: string;
      end: string;
      tz: string;
      limit?: number;
      cursor?: string;
    }
  ) {
    const urlParams = new URLSearchParams();
    urlParams.set("start", params.start);
    urlParams.set("end", params.end);
    urlParams.set("tz", params.tz);
    if (params.limit) {
      urlParams.set("limit", params.limit.toString());
    }
    if (params.cursor) {
      urlParams.set("cursor", params.cursor);
    }
    return this.requestWithStatus<IOfficesSchedules>(
      "GET",
      `/v1/resources/${encodeURIComponent(resourceId)}/dedicated_exceptions/schedules?${urlParams.toString()}`
    );
  }

  async allIndexed(
    filter: ScheduleFilterType | "",
    params: ScheduleParamsAll,
    useCore = false
  ): Promise<ResponseWithStatus<IOfficesSchedulesIndexed>> {
    const ret = await this.all(filter, params, useCore);
    const uniqueUsers: { [idx: string]: IUserFull } = {};
    const scheduleByUserByDay: {
      [idx: string]: { [day: string]: ISchedule[] };
    } = {};
    const schedulesByDay: { [day: string]: ISchedule[] } = {};
    const result = ret.result!;
    // The window this call actually queried — an open-ended schedule (e.g.
    // a still-checked-in walk-up) has no end to derive a day span from, so
    // it's indexed under every requested day on/after its start instead
    // (see getDaysWithinTimespan).
    const queryDays = params.date
      ? expandQueryDays(params.date, params.days ?? (params.weeks ?? 1) * 7)
      : undefined;
    (result.elements ?? []).forEach((x) => {
      const days = getDaysWithinTimespan(x.start, x.end, x.time_zone, queryDays);
      days.forEach((day) => {
        const user = x.user || x.organizer;
        uniqueUsers[user.id] = user;
        schedulesByDay[day] = schedulesByDay[day] ?? [];
        schedulesByDay[day].push(x);
        scheduleByUserByDay[user.id] = scheduleByUserByDay[user.id] ?? {};
        scheduleByUserByDay[user.id][day] =
          scheduleByUserByDay[user.id][day] ?? [];
        scheduleByUserByDay[user.id][day].push(x);
      });
    });
    return {
      ...ret,
      result: {
        ...result,
        dates: schedulesByDay,
        dates_user: scheduleByUserByDay,
        users: uniqueUsers
      }
    };
  }

  async allUsage(filter: ScheduleFilterType, params: ScheduleParamsAll) {
    const urlParams = this.formatScheduleParams(params);
    return this.requestWithStatus<IOfficeUsageFull>(
      "GET",
      `/schedules/${filter}/usage?${urlParams.toString()}`
    );
  }

  async workingLocations(params: ScheduleParamsAll = {}) {
    const urlParams = this.formatScheduleParams(params);
    return this.requestWithStatus<IWorkingLocations>(
      "GET",
      `/working_locations?${urlParams.toString()}`
    );
  }

  async workingLocationsDefaults(userId: string) {
    return this.requestWithStatus<IWorkingLocations>(
      "GET",
      `/working_locations/${userId}`
    );
  }

  async updateWorkingLocationDefaults(
    locations: {
      [dayIdx in WeekDayNames]?:
        | {
            location: IWorkingLocationPresenceType;
            office_id?: string | undefined;
          }
        | undefined;
    }
  ) {
    const body = {
      locations
    };
    return this.requestWithStatus("POST", "/working_locations", body);
  }

  async updateWorkingLocationDay(
    location: { location: IWorkingLocationPresenceType; office_id?: string },
    /** @deprecated ignored — the weekday is derived from `date` */
    day: number,
    date: string
  ) {
    // Derive the weekday from the date itself: callers pass the position in
    // the rendered week, which no longer maps to a fixed weekday now that the
    // week start day is configurable.
    const weekDayNumber = new Date(`${date}T00:00:00`).getDay();
    const dayIdx = ALL_WEEK_DAYS[(weekDayNumber + 6) % 7];
    const body = {
      locations: {
        [dayIdx]: location
      },
      start: `${date}T00:00:00Z`,
      end: `${date}T23:59:59Z`
    };
    return this.requestWithStatus("POST", "/working_locations", body);
  }

  async scheduleById(schedule_id: string) {
    return this.requestWithStatus<ISchedule>(
      "GET",
      `/v1/schedules/${schedule_id}`
    );
  }

  async schedulesById(
    schedule_ids: string[],
    office_id: string | null = null,
    filter: SchedulesFilter | null
  ) {
    let requestUrl = `/offices/${office_id}/schedules`;

    if (office_id == null) {
      requestUrl = `/schedules`;
    }

    for (let i = 0; i < schedule_ids.length; i++) {
      if (i === 0) {
        requestUrl += `?schedule=${encodeURIComponent(schedule_ids[i])}`;
      } else {
        requestUrl += `&schedule=${encodeURIComponent(schedule_ids[i])}`;
      }
    }

    if (filter) {
      requestUrl = this.processFilter(requestUrl, filter);
    }

    return this.requestWithStatus<IOfficesSchedules>("GET", requestUrl, null);
  }

  /**
   * [DEPRECATED]
   *
   * Shows whether a user has permission for schedule:respond. This should now be called by looking at
   * authenticatedUser.user.permissions.includes('schedule:respond')
   *
   */
  async canApprove(): Promise<boolean> {
    const user = await this.request("GET", "/user");
    const canApprove =
      user &&
      user.organization_user !== undefined &&
      user.organization_user.permissions.includes("schedule:respond");
    return canApprove;
  }

  /** @deprecated Use `all(...)` instead */
  public async pending(
    office_id: string,
    time_zone: string | null = null,
    date: string | null = null,
    params: {
      limit?: number;
      weeks?: number;
    } = {}
  ) {
    if (!this.canApprove()) {
      throw new Error("User can't call pending");
    }
    const urlParams = new URLSearchParams();
    if (date) {
      urlParams.set("date", date);
    }
    urlParams.set(
      "weeks",
      (params.weeks ?? Schedules.DEFAULT_NUMBER_OF_WEEKS).toString()
    );
    if (params.limit) {
      urlParams.set("limit", params.limit.toString());
    }

    return this.requestWithStatus<ResGetOfficesSchedulesPending>(
      "GET",
      `/offices/${office_id}/schedules/pending?${urlParams.toString()}`
    );
  }

  /** @deprecated Use `all(...)` instead */
  async usage(
    office_id: string,
    time_zone: string | null = null,
    date: string | null = null
  ): Promise<ResponseWithStatus<IOfficeUsageFormatted>> {
    const weekDate = date !== null ? encodeURIComponent(date) : null;
    const response =
      await this.requestWithStatus<ResOfficesSchedulesOccupiedUsage>(
        "GET",
        `/offices/${office_id}/schedules/occupied/usage?weeks=${Schedules.DEFAULT_NUMBER_OF_WEEKS}${
          weekDate !== null ? "&date=" + weekDate : ""
        }`
      );
    if (!response.ok || !response.result) {
      return response as unknown as ResponseWithStatus<IOfficeUsageFormatted>;
    }
    return { ...response, result: this.formatUsages(response.result) };
  }

  /** @deprecated Use `allUsage(...)` instead */
  async usageFull(
    office_id: string,
    time_zone: string | null = null,
    date: string | null = null
  ): Promise<ResponseWithStatus<IOfficeUsageFull>> {
    const response = await this.requestWithStatus<IOfficeUsageFull>(
      "GET",
      `/offices/${office_id}/schedules/occupied/usage?weeks=${Schedules.DEFAULT_NUMBER_OF_WEEKS}${
        date !== null ? "&date=" + encodeURIComponent(date) : ""
      }`
    );
    return response;
  }

  /** @deprecated Use `all(...)` instead */
  async occupied(
    office_id: string,
    time_zone: string | null = null,
    date: string | null = null,
    teams: string[] = [],
    resourceIds: string[] = [],
    limit = 1000,
    types: ResourceType[] = []
  ) {
    const weekDate = date !== null ? encodeURIComponent(date) : null;
    let query = `weeks=${Schedules.DEFAULT_NUMBER_OF_WEEKS}${weekDate !== null ? "&date=" + weekDate : ""}`;
    for (const teamId of teams) {
      query += `&team=${encodeURIComponent(teamId)}`;
    }
    for (const resourceId of resourceIds) {
      query += `&resource=${encodeURIComponent(resourceId)}`;
    }
    for (const type of types) {
      query += `&type=${encodeURIComponent(type)}`;
    }
    query += `&limit=${limit}`;
    return await this.requestWithStatus<ResGetOfficesSchedulesOccupied>(
      "GET",
      `/offices/${office_id}/schedules/occupied?${query}`
    );
  }

  /** @deprecated Use `all(...)` instead */
  async scheduler(
    office_id: string,
    time_zone: string | null = null,
    date: string | null = null,
    teams: string[] = [],
    resourceIds: string[] = [],
    limit = 1000
  ) {
    const weekDate = date !== null ? encodeURIComponent(date) : null;
    let query = `weeks=${Schedules.DEFAULT_NUMBER_OF_WEEKS}${weekDate !== null ? "&date=" + weekDate : ""}`;
    for (const teamId of teams) {
      query += `&team=${encodeURIComponent(teamId)}`;
    }
    for (const resourceId of resourceIds) {
      query += `&resource=${encodeURIComponent(resourceId)}`;
    }
    query += `&limit=${limit}`;
    return await this.requestWithStatus<ResGetOfficesSchedulesOccupied>(
      "GET",
      `/offices/${office_id}/schedules/scheduler?${query}`
    );
  }

  public formatUsages(usageResult: IOfficeUsageFull): IOfficeUsageFormatted {
    const usages = [];
    for (const date of Object.keys(usageResult.dates_type)) {
      const usagesByType = usageResult.dates_type[date];
      const hasDesks = typeof usagesByType.desk !== "undefined";
      const hasWorkspace = typeof usagesByType.workspace !== "undefined";
      const acceptedDesks =
        (hasDesks ? usagesByType.desk!.accepted : 0) +
        (hasWorkspace ? usagesByType.workspace!.accepted : 0);
      const freeDesks =
        (hasDesks ? usagesByType.desk!.free : 0) +
        (hasWorkspace ? usagesByType.workspace!.free : 0);
      const totalDesks =
        (hasDesks ? usagesByType.desk!.total : 0) +
        (hasWorkspace ? usagesByType.workspace!.total : 0);
      usages.push({
        date: date,
        day: date.substring(date.lastIndexOf("-") + 1),
        accepted: acceptedDesks,
        free: freeDesks,
        total: totalDesks,
        occupancy:
          totalDesks > 0 ? ((100 * acceptedDesks) / totalDesks).toFixed(0) : 0
      });
    }

    return {
      month: usageResult.months.start,
      usages: usages,
      days: usageResult.days,
      weekDate: usageResult.week_date,
      previousWeekDate: usageResult.previous_week_date,
      nextWeekDate: usageResult.next_week_date
    };
  }

  async respond(scheduleId: string, slotId: string | null, status: string) {
    // slotId is ignored here since we're approving/rejecting the whole schedule

    const endpoint =
      slotId !== null
        ? `${this.settings.adminAuth ? "/integrations" : ""}/schedules/${encodeURIComponent(scheduleId)}/slots/${encodeURIComponent(
            slotId
          )}/respond/${encodeURIComponent(status)}`
        : `${this.settings.adminAuth ? "/integrations" : ""}/schedules/${encodeURIComponent(scheduleId)}/respond/${encodeURIComponent(status)}`;

    const result = await this.requestWithStatus("PUT", endpoint);

    return { ...result, result: { scheduleId, slotId, status } };
  }

  /**
   * Checks a user into every resource of a reservation at once.
   *
   * Presence belongs to the user, not to a slot: a reservation can hold several
   * meeting rooms, and the person is present for the booking as a whole. Prefer this
   * over the slot-based {@link checkin}.
   */
  async checkinUser(scheduleId: string, userId: string) {
    return await this.requestWithStatus(
      "POST",
      `/v1/schedules/${encodeURIComponent(scheduleId)}/users/${encodeURIComponent(userId)}/presence`
    );
  }

  /** Checks a user out of every resource of a reservation. See {@link checkinUser}. */
  async checkoutUser(scheduleId: string, userId: string) {
    return await this.requestWithStatus(
      "DELETE",
      `/v1/schedules/${encodeURIComponent(scheduleId)}/users/${encodeURIComponent(userId)}/presence`
    );
  }

  /**
   * RSVP of a user for a whole reservation — the per-user counterpart of
   * {@link presence}, which answers for a single slot.
   */
  async userPresence(
    scheduleId: string,
    userId: string,
    presenceStatus: PresenceStatus,
    presenceType?: PresenceType
  ) {
    const url = `/v1/schedules/${encodeURIComponent(scheduleId)}/users/${encodeURIComponent(
      userId
    )}/presence/${encodeURIComponent(presenceStatus)}`;
    const data: { presence_type?: PresenceType } = {};

    if (presenceStatus === PRESENCE_STATUS_ACCEPTED && presenceType) {
      data.presence_type = presenceType;
    }

    const response = await this.requestWithStatus("PUT", url, data);

    return {
      ...response,
      result: { scheduleId, userId, presenceStatus, presenceType }
    };
  }

  /** @deprecated Checks in a single slot; use {@link checkinUser}. */
  async checkin(resourceId: string, scheduleId: string, slotId: string) {
    return await this.requestWithStatus(
      "POST",
      `/v1/resources/${encodeURIComponent(resourceId)}/presence/${encodeURIComponent(scheduleId)}/${encodeURIComponent(
        slotId
      )}`
    );
  }

  /** @deprecated Checks out a single slot; use {@link checkoutUser}. */
  async checkout(resourceId: string, scheduleId: string, slotId: string) {
    return this.requestWithStatus(
      "DELETE",
      `/v1/resources/${encodeURIComponent(resourceId)}/presence/${encodeURIComponent(scheduleId)}/${encodeURIComponent(
        slotId
      )}`
    );
  }

  async delete({
    schedule,
    slot,
    recurring = false,
    slug = "",
    resourceId = ""
    // ? on slug is to accommodate mobile types before the slug was added
  }: {
    slug?: string;
    schedule: string;
    slot?: string;
    recurring?: boolean;
    resourceId?: string;
  }) {
    const body = recurring ? { recurring } : {};
    const newBasePath = getUrl({
      slug,
      officeId: undefined,
      method: "DELETE",
      resourceId
    });
    const oldBasePath = `${this.settings.adminAuth ? "/integrations" : ""}/schedules/`;
    const basePath = newBasePath || oldBasePath;

    const deletePath = slot
      ? `${basePath}${encodeURIComponent(schedule)}/slots/${encodeURIComponent(slot)}`
      : `${basePath}${encodeURIComponent(schedule)}`;

    return this.requestWithStatus("DELETE", deletePath, body);
  }

  /** @deprecated Answers for a single slot; use {@link userPresence}. */
  async presence(
    scheduleId: string,
    slotId: string,
    presenceStatus: PresenceStatus,
    presenceType?: PresenceType,
    skipIntegrationEvents?: boolean
  ) {
    let url = `/v1/schedules/${encodeURIComponent(scheduleId)}/slots/${encodeURIComponent(
      slotId
    )}/presence/${encodeURIComponent(presenceStatus)}`;
    const data: { presence_type?: PresenceType } = {};

    if (skipIntegrationEvents) {
      url += "?skip-integration-events=true";
    }

    if (presenceStatus === PRESENCE_STATUS_ACCEPTED && presenceType) {
      data.presence_type = presenceType;
    }

    const response = await this.requestWithStatus("PUT", url, data);

    // slotId is ignored here since we're approving/rejecting the whole schedule
    return {
      ...response,
      result: { scheduleId, slotId, presenceStatus, presenceType }
    };
  }

  create(
    officeId: string,
    request: ScheduleRequest & {
      // ? on time_zone is to accommodate mobile types before the slug was added
      time_zone?: string;
    },
    isFromCalendar = false,
    slug = ""
  ) {
    const resId = request.schedule_slots[0].resource_id;
    const newUrl = getUrl({
      slug,
      officeId,
      method: "POST",
      resourceId: resId
    });

    let url = newUrl ?? `/offices/${encodeURIComponent(officeId)}/schedules`;
    if (isFromCalendar) {
      url += "?is-from-calendar=true";
    }

    return this.requestWithStatus<
      | (Id & { elements: ISchedule[] })
      | { context: { daysOpen: number[]; errors?: { [slot: string]: string } } }
      | { errors: { slots: string } }
    >("POST", url, request);
  }

  patch(
    scheduleId: string,
    request: ScheduleRequestUpdate,
    isFromCalendar = false,
    orgSlug = ""
  ) {
    const resId = request.schedule_slots?.[0]?.resource_id ?? "";
    const newUrl = getUrl({
      slug: orgSlug,
      method: "PATCH",
      officeId: undefined,
      scheduleId,
      resourceId: resId
    });
    let url = newUrl || `/schedules/${encodeURIComponent(scheduleId)}`;

    if (isFromCalendar) {
      url += "?is-from-calendar=true";
    }

    return this.requestWithStatus<
      | Id
      | { context: { daysOpen: number[]; errors?: { [slot: string]: string } } }
      | { errors: { slots: string } }
    >("PATCH", url, request);
  }

  put(
    scheduleId: string,
    request: ScheduleRequestUpdate,
    isFromCalendar = false,
    orgSlug = ""
  ) {
    const resId = request.schedule_slots?.[0]?.resource_id ?? "";
    const newUrl = getUrl({
      slug: orgSlug,
      officeId: undefined,
      method: "PATCH",
      scheduleId,
      resourceId: resId
    });

    let url = newUrl || `/schedules/${encodeURIComponent(orgSlug)}`;
    if (isFromCalendar) {
      url += "?is-from-calendar=true";
    }

    return this.requestWithStatus<
      | Id
      | { context: { daysOpen: number[] }; errors?: { [slot: string]: string } }
      | { errors: { slots: string } }
    >("PUT", url, request);
  }

  addToSchedule(
    officeId: string,
    scheduleId: string,
    request: ScheduleRequest
  ): Promise<ResponseWithStatus<Id>> {
    return this.requestWithStatus<Id>(
      "PUT",
      `/offices/${encodeURIComponent(officeId)}/schedules/${encodeURIComponent(scheduleId)}`,
      request
    );
  }

  createOrUpdateFromIntegrations(
    authorization: string,
    resourceId: string,
    request: CreateOrUpdateScheduleFromIntegrationsRequest,
    isFromCalendar: boolean,
    scheduleId?: string | null
  ): Promise<ResponseWithStatus<Id>> {
    if (scheduleId === undefined) {
      scheduleId = null;
    }

    let url = `/integrations/resources/${encodeURIComponent(resourceId)}/schedules`;
    if (scheduleId !== null) {
      url += `/${scheduleId}`;
    }
    if (isFromCalendar) {
      url += "?is-from-calendar=true";
    }

    return this.requestWithStatus<Id>(
      scheduleId !== null ? "PUT" : "POST",
      url,
      request,
      {
        authHeader: `Basic ${authorization}`
      }
    );
  }

  /**
   * @deprecated
   */
  async add(request: {
    office_id: string;
    resource_id: string;
    start: string;
    time_zone: string;
    duration: number;
    users: string[];
    teams: string[];
  }) {
    const period: Period = this.createPeriod(
      request.start,
      request.duration,
      request.time_zone
    );

    const scheduleRequest: ScheduleRequest & {
      time_zone: string;
    } = {
      schedule_slots: [],
      time_zone: period.time_zone
    };

    request.users.forEach((userId) => {
      scheduleRequest.schedule_slots.push({
        resource_id: request.resource_id,
        user_id: userId,
        team_id: null,
        slots: 1,
        start: period.start,
        end: period.end,
        time_zone: period.time_zone
      });
    });

    request.teams.forEach((teamId) => {
      scheduleRequest.schedule_slots.push({
        resource_id: request.resource_id,
        user_id: null,
        team_id: teamId,
        slots: 1,
        start: period.start,
        end: period.end,
        time_zone: period.time_zone
      });
    });

    const response = await this.create(request.office_id, scheduleRequest);
    if (!response.ok || response.result === null) {
      throw response;
    }
    return response.result;
  }

  async existsById(
    authorization: string,
    organizationId: string,
    scheduleId: string
  ) {
    return this.requestWithStatus<{ exists: boolean }>(
      "GET",
      `/organizations/${organizationId}/schedules/${scheduleId}/exists`,
      null,
      {
        authHeader: `Basic ${authorization}`
      }
    );
  }
}

type PossibleData = {
  scheduleId?: string;
  officeId?: string;
};
const getEndpointForMethod = {
  core: {
    POST: () => "/v1/schedules",
    PATCH: ({ scheduleId = "" }: PossibleData) =>
      `/v1/schedules/${encodeURIComponent(scheduleId)}`,
    PUT: ({ scheduleId = "" }: PossibleData) =>
      `/v1/schedules/${encodeURIComponent(scheduleId)}`,
    DELETE: ({ scheduleId = "" }: PossibleData) =>
      `/v1/schedules/${encodeURIComponent(scheduleId)}`
  },
  api: {
    POST: ({ officeId = "" }: PossibleData) =>
      `/offices/${encodeURIComponent(officeId)}/schedules`,
    PATCH: ({ scheduleId = "" }: PossibleData) =>
      `/schedules/${encodeURIComponent(scheduleId)}`,
    PUT: ({ scheduleId = "" }: PossibleData) =>
      `/schedules/${encodeURIComponent(scheduleId)}`,
    DELETE: ({ scheduleId = "" }: PossibleData) =>
      `/schedules/${encodeURIComponent(scheduleId)}`
  }
};

const getUrl = ({
  slug,
  officeId,
  resourceId = "",
  method,
  scheduleId
}: {
  slug: string;
  officeId: string | undefined;
  resourceId: string;
  method: "POST" | "PATCH" | "PUT" | "DELETE";
  scheduleId?: string;
}) => {
  return getEndpointForMethod.core[method]({
    officeId,
    scheduleId
  });
};
